import { useQuery } from "@tanstack/react-query";
import { fetchSubscriptionSummary } from "@/services/subscriptionApi";
import { isUnauthorizedPlatformApiError } from "@/services/platformApi/client";
import { useAuth } from "@/contexts/auth";
import { useLocation } from "react-router-dom";
import { SubscriptionStatus, DEFAULT_STALE_TIME, SUBSCRIPTION_PAGE_STALE_TIME } from "./types";
import { debugLogger } from '@/utils/debug';

/**
 * Hook to check and retrieve a user's current subscription status
 */
export function useSubscriptionStatus() {
  const { user, isAdmin, loading } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isSubscriptionPage = location.pathname.includes('/subscription') || (
    location.pathname === '/profile' && searchParams.get('tab') === 'subscription'
  );

  const queryResult = useQuery({
    queryKey: ["subscription", user?.id, isAdmin],
    queryFn: async (): Promise<SubscriptionStatus> => {
      if (!user) throw new Error("Not authenticated");
      
      // If user is admin, return subscribed status as true without making any calls
      if (isAdmin) {
        debugLogger.log("Admin user detected, skipping subscription query");
        return {
          subscribed: true,
          status: 'active',
          subscription_end: null,
          subscription_source: 'admin',
          tier: 'premium_tier',
          planName: 'Premium',
          manageable: false,
        };
      }

      try {
        debugLogger.log("Querying subscription summary for user", user.id);
        return await fetchSubscriptionSummary();
      } catch (error) {
        if (!isUnauthorizedPlatformApiError(error)) {
          console.error("Error in subscription check:", error);
        }
        return {
          subscribed: false,
          status: 'free',
          subscription_source: null,
          tier: 'free_tier',
          planName: 'Free',
        };
      }
    },
    enabled: !loading && !!user,
    refetchInterval: isSubscriptionPage ? SUBSCRIPTION_PAGE_STALE_TIME : false, // Only auto-refetch on subscription page
    refetchOnMount: true,
    refetchOnWindowFocus: isSubscriptionPage, // Only refetch on window focus if on subscription page
    staleTime: isSubscriptionPage ? SUBSCRIPTION_PAGE_STALE_TIME : DEFAULT_STALE_TIME,
    gcTime: DEFAULT_STALE_TIME * 2, // Cache for twice as long as stale time (was cacheTime)
  });
  
  return queryResult;
}
